import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Descriptions,
  Table,
  Tag,
  Button,
  Spin,
  message,
  Card,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Popconfirm,
  Tooltip,
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  getUser,
  updateUser,
  getOrganizations,
  getSims,
  removeSimAssignment,
  assignSimToUser,
  Organization,
  User,
  UnifiedSim,
} from '../../services/adminService';
import { ApiClient } from '../../services/apiClient';
import { useAuth } from '../../auth/AuthContext';
import LookupSelect from '../../components/LookupSelect';
import PageHelp from '../../components/PageHelp';
import UserForm from '../../components/UserForm';
import { providerLabel } from '../../utils/providerLabel';

const GLOBAL_ROLE_HELP =
  'Global role — system-wide permission level. Determines which API endpoints the user can call, and the scope of their queries (their org + descendants). Each user has one global role.';
const ROLE_ON_ASSET_HELP =
  "Role on Asset — a per-asset label describing the user's job on this specific yacht (captain, engineer, crew, etc.). Informational only; it does not grant permissions. A user can have a different Role on Asset for each yacht they're assigned to.";

interface SimAssignment {
  id: number;
  identifier: string;
  provider: string;
  sim_type: string | null;
  asset_id: number | null;
  asset_name: string | null;
  organization_id: number;
  assigned_at: string;
}

interface AssetAssignment {
  id: number;
  asset_id: number;
  asset_name: string;
  asset_type: string;
  department: string | null;
  role_on_asset: string;
  created_at: string;
}

interface UserDetailData {
  user: User & { cognito_sub: string };
  simAssignments: SimAssignment[];
  assetAssignments: AssetAssignment[];
}

const roleColors: Record<string, string> = {
  super_admin: 'red',
  reseller_admin: 'volcano',
  reseller_support: 'orange',
  customer_admin: 'blue',
  asset_manager: 'cyan',
  department_head: 'green',
  crew: 'default',
};

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role: myRole } = useAuth();
  const [data, setData] = useState<UserDetailData | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [assignSimVisible, setAssignSimVisible] = useState(false);
  const [availableSims, setAvailableSims] = useState<UnifiedSim[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [editForm] = Form.useForm();
  const [assignSimForm] = Form.useForm();

  const userId = Number(id);
  const canEdit =
    myRole === 'super_admin' || myRole === 'reseller_admin' || myRole === 'customer_admin';

  const fetchData = async () => {
    setLoading(true);
    try {
      const [userData, orgs] = await Promise.all([
        ApiClient.get<UserDetailData>(`/admin/users/${userId}`),
        getOrganizations(),
      ]);
      setData(userData);
      setOrganizations(orgs);
    } catch (err: any) {
      message.error(err.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [userId]);

  const handleEdit = async (values: any) => {
    setSubmitting(true);
    try {
      await updateUser(userId, values);
      message.success('User updated');
      setEditModalVisible(false);
      fetchData();
    } catch (err: any) {
      message.error(err.message || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = () => {
    if (!data) return;
    setEditModalVisible(true);
  };

  const handleUnassignSim = async (assignmentId: number) => {
    try {
      await removeSimAssignment(assignmentId);
      message.success('SIM unassigned from user');
      fetchData();
    } catch (err: any) {
      message.error(err.message || 'Failed to unassign SIM');
    }
  };

  const openAssignSim = async () => {
    try {
      const allSims = await getSims();
      // Show SIMs allocated to user's org but not assigned to any user
      setAvailableSims(
        allSims.filter(
          s =>
            s.allocation &&
            s.allocation.organizationId === data?.user.organization_id &&
            !s.allocation.userId
        )
      );
    } catch {
      setAvailableSims([]);
    }
    assignSimForm.resetFields();
    setAssignSimVisible(true);
  };

  const handleAssignSim = async (values: any) => {
    const sim = availableSims.find(s => `${s.provider}:${s.identifier}` === values.simKey);
    if (!sim) return;
    setSubmitting(true);
    try {
      await assignSimToUser({
        identifier: sim.identifier,
        provider: sim.provider,
        userId,
        simType: values.simType,
      });
      message.success('SIM assigned to user');
      setAssignSimVisible(false);
      fetchData();
    } catch (err: any) {
      message.error(err.message || 'Failed to assign SIM');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 24 }}>
        <p>User not found.</p>
        <Button onClick={() => navigate('/admin/users')}>Back to Users</Button>
      </div>
    );
  }

  const { user, simAssignments, assetAssignments } = data;

  const simColumns = [
    {
      title: 'Identifier',
      dataIndex: 'identifier',
      key: 'identifier',
      render: (id: string, record: SimAssignment) =>
        record.provider === 'vrs' ? (
          <Link to={`/admin/sims/${record.provider}/${id}`}>{id}</Link>
        ) : (
          id
        ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (p: string) => <Tag color={p === 'vrs' ? 'blue' : 'green'}>{providerLabel(p)}</Tag>,
    },
    {
      title: 'SIM Type',
      dataIndex: 'sim_type',
      key: 'sim_type',
      render: (val: string | null) => (val ? <Tag color="purple">{val}</Tag> : '-'),
    },
    {
      title: 'Asset',
      key: 'asset',
      render: (_: any, record: SimAssignment) =>
        record.asset_id ? (
          <Link to={`/admin/assets/${record.asset_id}`}>
            {record.asset_name || `Asset #${record.asset_id}`}
          </Link>
        ) : (
          '-'
        ),
    },
    {
      title: 'Assigned',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (val: string) => (val ? new Date(val).toLocaleDateString() : '-'),
    },
    ...(canEdit
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: SimAssignment) => (
              <Popconfirm
                title="Unassign this SIM from user?"
                onConfirm={() => handleUnassignSim(record.id)}
              >
                <Button type="link" danger size="small">
                  Unassign
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  const assetColumns = [
    {
      title: 'Asset',
      key: 'asset',
      render: (_: any, record: AssetAssignment) => (
        <Link to={`/admin/assets/${record.asset_id}`}>{record.asset_name}</Link>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'asset_type',
      key: 'asset_type',
      render: (val: string) => (val ? <Tag>{val}</Tag> : '-'),
    },
    {
      title: (
        <Space size={4}>
          Role on Asset
          <Tooltip title={ROLE_ON_ASSET_HELP}>
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'role_on_asset',
      key: 'role_on_asset',
      render: (val: string) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (val: string | null) => val || '-',
    },
    {
      title: 'Since',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (val: string) => (val ? new Date(val).toLocaleDateString() : '-'),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/users')}
          style={{ paddingLeft: 0 }}
        >
          Back to Users
        </Button>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>
          {user.first_name} {user.last_name}{' '}
          <Tag color={roleColors[user.role] || 'default'}>{user.role}</Tag>{' '}
          <PageHelp
            title="User Details Help"
            sections={[
              {
                title: 'User Profile',
                content:
                  'View and edit user information. Changes to name and role are synced to the authentication system automatically.',
              },
              {
                title: 'Two kinds of "role"',
                content:
                  "A user has TWO different role concepts, and they don't mean the same thing:\n\n\u2022 Global Role (in User Information) \u2014 system-wide permission level (super_admin, reseller_admin, customer_admin, asset_manager, department_head, crew). Controls which API actions the user can take and the scope of data they see. Each user has exactly one.\n\n\u2022 Role on Asset (in Asset Assignments) \u2014 a per-yacht descriptor of the user's job on that boat (captain, engineer, chef, etc.). Purely informational; does not grant permissions. A user can have a different Role on Asset for every yacht they're crew on.",
                tips: [
                  'Example: a super_admin who is "engineer" on Odessa II for support purposes',
                  'Example: a customer_admin who is "captain" on her own yacht',
                  'The Tag in the page header shows the Global Role',
                ],
              },
              {
                title: 'Asset Assignments',
                content:
                  'Shows which assets (yachts) this user is assigned to as crew. Add/remove these from the Asset Detail page \u2192 Crew section. The "Role on Asset" column is the per-asset job label, NOT the global role.',
                tips: [
                  'asset_manager (global role) sees all yacht-level SIMs in their org scope',
                  'crew (global role) sees only SIMs assigned directly to them',
                  'See "Visibility flags" on Edit User for finer control',
                ],
              },
              {
                title: 'SIM Cards',
                content:
                  'SIM cards personally assigned to this user. Use "Assign SIM" to give them a SIM from the org pool, or "Unassign" to remove one.\n\nOnly SIMs allocated to the user\'s organization (but not yet assigned to another user) are available.',
                tips: [
                  'Unassigning keeps the SIM in the org \u2014 it does not deallocate it',
                  'Both Data and Crew SIMs can be assigned',
                ],
              },
            ]}
          />
        </h2>
        {canEdit && (
          <Button type="primary" icon={<EditOutlined />} onClick={openEditModal}>
            Edit User
          </Button>
        )}
      </div>

      <Card title="User Information" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="ID">{user.id}</Descriptions.Item>
          <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
          <Descriptions.Item label="First Name">{user.first_name}</Descriptions.Item>
          <Descriptions.Item label="Last Name">{user.last_name}</Descriptions.Item>
          <Descriptions.Item
            label={
              <Space size={4}>
                Role
                <Tooltip title={GLOBAL_ROLE_HELP}>
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </Space>
            }
          >
            <Tag color={roleColors[user.role] || 'default'}>{user.role}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Department">{user.department || '-'}</Descriptions.Item>
          <Descriptions.Item label="Organization">
            <Link to={`/admin/organizations/${user.organization_id}`}>{user.org_name}</Link>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={user.is_active ? 'green' : 'red'}>
              {user.is_active ? 'Active' : 'Inactive'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Last Login">
            {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {new Date(user.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Updated">
            {new Date(user.updated_at).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Asset Assignments (Crew on) */}
      <Card title={`Asset Assignments (${assetAssignments.length})`} style={{ marginBottom: 24 }}>
        {assetAssignments.length > 0 ? (
          <Table
            columns={assetColumns}
            dataSource={assetAssignments}
            rowKey="id"
            pagination={false}
            size="small"
          />
        ) : (
          <div style={{ color: '#999', textAlign: 'center', padding: 16 }}>
            Not assigned to any assets
          </div>
        )}
      </Card>

      {/* SIM Assignments */}
      <Card
        title={`SIM Cards (${simAssignments.length})`}
        extra={
          canEdit && (
            <Button type="primary" size="small" icon={<EditOutlined />} onClick={openAssignSim}>
              Assign SIM
            </Button>
          )
        }
      >
        {simAssignments.length > 0 ? (
          <Table
            columns={simColumns}
            dataSource={simAssignments}
            rowKey="id"
            pagination={false}
            size="small"
          />
        ) : (
          <div style={{ color: '#999', textAlign: 'center', padding: 16 }}>
            No SIM cards assigned
          </div>
        )}
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit User"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
      >
        <UserForm
          form={editForm}
          mode="edit"
          organizations={organizations}
          initialValues={data?.user}
          onFinish={handleEdit}
        />
        <div style={{ marginTop: 16 }}>
          <Space>
            <Button type="primary" loading={submitting} onClick={() => editForm.submit()}>
              Save Changes
            </Button>
            <Button onClick={() => setEditModalVisible(false)}>Cancel</Button>
          </Space>
        </div>
      </Modal>

      {/* Assign SIM Modal */}
      <Modal
        title={`Assign SIM to ${user.first_name} ${user.last_name}`}
        open={assignSimVisible}
        onCancel={() => setAssignSimVisible(false)}
        footer={null}
      >
        {availableSims.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 16, color: '#999' }}>
            No unassigned SIMs available in {user.org_name}. Allocate SIMs to the organization first
            via Inventory.
          </div>
        ) : (
          <Form form={assignSimForm} layout="vertical" onFinish={handleAssignSim}>
            <Form.Item
              name="simKey"
              label="SIM Card"
              rules={[{ required: true, message: 'Select a SIM' }]}
            >
              <Select placeholder="Select SIM" showSearch optionFilterProp="children">
                {availableSims.map(s => (
                  <Select.Option
                    key={`${s.provider}:${s.identifier}`}
                    value={`${s.provider}:${s.identifier}`}
                  >
                    [{providerLabel(s.provider)}] {s.identifier} {s.alias ? `(${s.alias})` : ''}{' '}
                    {s.allocation?.assetName ? `on ${s.allocation.assetName}` : ''}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="simType"
              label="SIM Type"
              rules={[{ required: true, message: 'Select SIM type' }]}
            >
              <LookupSelect category="sim_type" placeholder="e.g. crew, personal" />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  Assign
                </Button>
                <Button onClick={() => setAssignSimVisible(false)}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default UserDetail;
